<template>
  <div class="modals">
    <div id="file-modal">
      <sweet-modal
        overlay-theme="dark"
        ref="view_file"
        hide-close-button
        enable-mobile-fullscreen
        class="file-preview"
        width="100%"
        @open="viewFileModalOpen"
        @close="viewFileModalClose"
      >
        <div class="flex flex-col md:flex-row">
          <!-- preview, with the prev / next arrows over its edges -->
          <div class="flex-1 relative">
            <div v-if="!preview_loading" class="h-full overflow-hidden">
              <iframe
                :src="preview_url"
                frameborder="0"
                width="100%"
                height="100%"
                class="iframe"
                @load="iframeLoaded"
              ></iframe>
            </div>
            <div v-else class="flex justify-center items-center h-full">
              <p>
                <i class="fa fa-circle-notch fa-spin"></i> Loading, please
                wait...
              </p>
            </div>

            <div
              class="absolute inset-y-0 left-0 flex items-center pl-2"
              v-show="paginate.prev && withNavigation"
            >
              <a href="#" class="w-12 h-12 rounded-full bg-white border shadow flex items-center justify-center text-grey-darker no-underline hover:bg-grey-lighter hover:text-brand" title="Previous document" @click.prevent="getFile('prev')">
                <i class="fa fa-chevron-left"></i>
              </a>
            </div>
            <div
              class="absolute inset-y-0 right-0 flex items-center pr-2"
              v-show="paginate.next && withNavigation"
            >
              <a href="#" class="w-12 h-12 rounded-full bg-white border shadow flex items-center justify-center text-grey-darker no-underline hover:bg-grey-lighter hover:text-brand" title="Next document" @click.prevent="getFile('next')">
                <i class="fa fa-chevron-right"></i>
              </a>
            </div>
          </div>

          <form @submit.prevent="finishTagging" class="flex-1 flex flex-col p-4 text-left">
            <!-- header: filename, duplicate badge, delete -->
            <div class="flex items-start justify-between">
              <div class="min-w-0 flex-1 pr-4">
                <p class="font-medium text-xl truncate" :title="file.filename">{{ file.filename }}</p>
                <p class="mt-1 text-sm" v-if="file.duplicate_file_id && duplicate_file">
                  <span class="inline-block rounded-full bg-orange-lightest text-orange text-xxs uppercase tracking-wide font-semibold px-2 py-1 mr-2">
                    Possible duplicate
                  </span>
                  <a class="text-brand" href="#" @click.prevent="openFile(duplicate_file)">{{ duplicate_file.filename }}</a>
                </p>
              </div>
              <div
                class="flex-none"
                v-is-authorized="{
                  roles: [$store.getters.getUserRole],
                  permissions: [getCurrentStateFilePermissions()],
                }"
              >
                <button
                  type="button"
                  class="border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded hover:bg-red hover:text-white"
                  :class="{ 'cursor-not-allowed opacity-50': deleting }"
                  :disabled="deleting"
                  @click.prevent="deleteFileConfirm()"
                >
                  <span v-if="deleting"><i class="fa fa-spinner fa-spin mr-1"></i> Deleting</span>
                  <span v-else>Delete</span>
                </button>
              </div>
            </div>

            <p class="mt-2 pb-3 border-b text-sm text-grey-darker">
              <span v-if="file.uploader != undefined">
                <i class="fa fa-user text-brand mr-1"></i>
                {{ file.uploader.first_name }} {{ file.uploader.last_name }}
              </span>
              <span class="ml-4"><i class="fa fa-file-alt text-brand mr-1"></i> {{ file_size }}</span>
              <span class="ml-4" :title="uploaded_date_formatted">
                <i class="fa fa-calendar-check text-brand mr-1"></i> {{ uploaded_date_simplified }}
              </span>
            </p>

            <div
              class="mt-3 text-sm"
              v-if="
                file.important_file &&
                file.important_file.is_important &&
                file.key_documents
              "
            >
              <p class="font-medium"><i class="fas fa-key text-brand mr-1"></i> Key documents</p>
              <ul class="list-disc list-inside text-grey-darker">
                <li v-for="keyDocument in file.key_documents" :key="keyDocument.id">
                  {{ keyDocument.title }}
                </li>
              </ul>
            </div>

            <!-- fields like the File and Tag page: label, hint, input underneath -->
            <div class="mt-4">
              <label class="font-medium" for="view_filing_datestamp">
                Date Stamp
                <span class="text-xs text-red font-normal ml-1">Required</span>
              </label>
              <div class="flex mt-1">
                <flat-pickr
                  class="appearance-none w-full h-10 border rounded px-3 text-black rounded-tr-none rounded-br-none"
                  :class="{ 'border-red': errors.has('filing_datestamp') }"
                  placeholder="Select Date"
                  :config="flatpickr_config"
                  :events="[
                  'on-change',
                  'on-close',
                  'on-destroy',
                  'on-key-down',
                  'on-month-change',
                  'on-open',
                  'on-parse-config',
                  'on-ready',
                  'on-value-update',
                ]"
                  v-validate
                  data-vv-rules="required|date_format:DD/MM/YYYY"
                  data-vv-value-path="filing_datestamp"
                  data-vv-name="filing_datestamp"
                  v-model="filing_datestamp"
                  name="filing_datestamp"
                ></flat-pickr>
                <button
                  type="button"
                  class="text-white h-10 px-4 rounded-tr rounded-br"
                  :class="{
                    'bg-red': errors.has('filing_datestamp'),
                    'bg-brand': !errors.has('filing_datestamp'),
                  }"
                  data-toggle
                >
                  <i class="fa fa-calendar"></i>
                </button>
              </div>
              <p class="text-red text-sm mt-1" v-if="errors.has('filing_datestamp')">
                {{ errors.first("filing_datestamp") }}
              </p>
            </div>

            <div class="mt-4">
              <label class="font-medium" for="view_filing_description">
                Description
                <span class="text-xs text-grey-darker font-normal ml-1">Optional</span>
              </label>
              <textarea
                class="appearance-none border rounded w-full py-2 px-3 mt-1 text-black"
                id="view_filing_description"
                placeholder="Description"
                rows="2"
                v-model="description"
              ></textarea>
            </div>

            <!-- tags: category list on the left, chips on the right -->
            <div class="mt-4 border rounded tag-height overflow-hidden relative">
              <div
                class="loading-indicator absolute inset-0 z-10"
                v-if="file.is_ml_processing"
              >
                <div class="w-full h-full flex justify-center items-center">
                  <span>
                    <i class="fa fa-circle-notch fa-spin"></i> Processing Tags
                  </span>
                </div>
              </div>
              <tabs :file-data="selected">
                <tab
                  v-for="(category, index) in categorized_tags"
                  :key="index"
                  :name="category.name"
                  :category="category"
                  :selected="index == 0 ? true : false"
                >
                  <tag
                    v-for="tag in category.tags"
                    :key="tag.id"
                    class="py-2"
                    :tag="tag"
                    :selected-files="selected"
                    used-for="file-and-tag"
                    @tagged="tagFiles"
                    @untagged="untagFiles"
                    :truncate="true"
                  ></tag>
                </tab>
              </tabs>
            </div>

            <!-- action bar -->
            <div class="mt-4 pt-4 border-t flex flex-col md:flex-row md:items-center md:justify-between">
              <a
                href="#"
                :download="download"
                target="_blank"
                class="border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white no-underline text-center"
                v-is-authorized="{ roles: [role], permissions: ['can_download'] }"
                @click.prevent="download"
              >
                <i class="fa fa-download mr-1"></i> Download
              </a>

              <div class="flex mt-2 md:mt-0">
                <button type="button" class="flex-1 md:flex-none border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded hover:bg-grey-lightest" @click="$refs.view_file.close()">
                  Close
                </button>

                <div class="flex flex-1 md:flex-none" v-if="!file.is_filed">
                  <button
                    v-if="paginate.next"
                    type="button"
                    class="flex-1 ml-2 border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white whitespace-no-wrap"
                    :class="{ 'cursor-not-allowed opacity-50': filing }"
                    :disabled="filing"
                    @click.prevent="
                      get_next_after_filing = true;
                      finishTagging();
                    "
                  >
                    File &amp; next
                  </button>
                  <button
                    type="submit"
                    class="flex-1 ml-2 bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark whitespace-no-wrap"
                    :class="{ 'cursor-not-allowed opacity-50': filing }"
                    :disabled="filing"
                  >
                    <span v-if="filing"><i class="fa fa-spinner fa-spin mr-1"></i> Filing</span>
                    <span v-else><i class="fa fa-folder mr-1"></i> File document</span>
                  </button>
                </div>

                <div
                  class="flex flex-1 md:flex-none"
                  v-else
                  v-is-authorized="{ roles: [role], permissions: ['can_update_document'] }"
                >
                  <button
                    v-if="paginate.next"
                    type="button"
                    class="flex-1 ml-2 border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white whitespace-no-wrap"
                    :class="{ 'cursor-not-allowed opacity-50': filing }"
                    :disabled="filing"
                    @click.prevent="
                      get_next_after_updating = true;
                      finishTagging();
                    "
                  >
                    Update &amp; next
                  </button>
                  <button
                    type="submit"
                    class="flex-1 ml-2 bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark whitespace-no-wrap"
                    :class="{ 'cursor-not-allowed opacity-50': filing }"
                    :disabled="filing"
                  >
                    <span v-if="updating"><i class="fa fa-spinner fa-spin mr-1"></i> Updating</span>
                    <span v-else><i class="fa fa-save mr-1"></i> Update file</span>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </sweet-modal>
    </div>

    <sweet-modal ref="view_ocr" overlay-theme="dark" title="OCR">
      <div class="p-8">
        <span v-if="ocr != ''">{{ ocr }}</span>
        <span v-else>OCR is not available</span>
      </div>
    </sweet-modal>

    <confirm-dialog
      ref="delete_file_confirm"
      title="Delete this document?"
      body="The document is deleted permanently."
      action-label="Delete"
      @confirm="deleteFile"
    ></confirm-dialog>

    <sweet-modal
      overlay-theme="dark"
      ref="file_loading"
      enable-mobile-fullscreen
      blocking
      pulse-on-block
      hide-close-button
    >
      <div class="mt-4">
        <p class="text-6xl text-grey-dark">
          <i class="fa fa-spin fa-circle-notch"></i>
        </p>
        Loading File
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Tag from "./Tagging/Tag";
import Tabs from "./FileView/Tabs";
import Tab from "./FileView/Tab";
import flatPickr from "vue-flatpickr-component";
import ConfirmDialog from "./ConfirmDialog.vue";

export default {
  components: {
    ConfirmDialog,
    Tag,
    Tabs,
    Tab,
    flatPickr,
  },

  props: {
    viewer_base: {
      type: String,
      default: "/pdf_viewer_3/viewer.html?file=",
    },
    withNavigation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      file: {},
      selected: "",
      preview_url: "",
      download_url: "",
      preview_loading: false,

      filing_datestamp: "",
      description: "",

      filing: false,
      updating: false,
      deleting: false,

      flatpickr_config: {
        wrap: true,
        allowInput: true,
        dateFormat: "d/m/Y",
        defaultDate: new Date(),
        monthSelectorType: "static",
      },

      ocr: "",

      tags: {},

      paginate: {
        prev: true,
        next: true,
      },

      get_next_after_filing: false,
      get_next_after_updating: false,

      tablet_spacing: false,

      iframe_loaded: false,
      iframe_reload_interval: null,
      iframe_reload_count: 0,
      duplicate_file: null,
    };
  },

  mounted() {
    this.$ebus.$on("close-file-view-modal", () => {
      if (!_.isNil(this.$refs.view_file)) {
        this.$refs.view_file.close();
      }
      this.$nextTick(() => {
        if (!_.isNil(this.$refs.file_loading)) {
          this.$refs.file_loading.open();
        }
      });
    });
  },

  created() {
    // window.addEventListener("orientationchange", () => {
    //   console.log(screen.orientation.angle, this.$mq);
    //   if (screen.orientation.angle == 90 && this.$mq == 'tablet') {
    //     this.tablet_spacing = true;
    //   }
    // });

    this.filing_datestamp = new Date();

    this.$ebus.$on("apply-paginate", (payload) => {
      this.paginate.prev = payload.prev;
      this.paginate.next = payload.next;
    });

    this.$ebus.$on("view-file", (file) => {
      if (!file || !file.id) {
        return;
      }
      if (
        this.$refs.view_file != undefined &&
        this.$refs.file_loading != undefined
      ) {
        this.description = "";
        this.preview_loading = true;
        this.get_next_after_filing = false;
        this.get_next_after_updating = false;

        axios
          .get(`api/vaults/${file.vault_id}/files/${file.id}/download-path`, {
            params: {
              is_download: 0,
            },
          })
          .then((resp) => {
            this.preview_loading = false;
            let data = resp.data;
            if (/\.pdf$/i.test(this.file.filename)) {
              this.preview_url =
                this.viewer_base + encodeURIComponent(data.url);
            } else {
              let office_file_exts = [
                "doc",
                "docx",
                "dotx",
                "odt",
                "ppt",
                "pptx",
                "odp",
                "xls",
                "xlsx",
                "ods",
                "rtf",
              ];
              let file_ext_matches = this.file.filename.match(/[^.]+$/);
              if (file_ext_matches.length > 0) {
                let file_ext = file_ext_matches[0];
                if (office_file_exts.indexOf(file_ext) >= 0) {
                  let baseURL = "https://docs.google.com/gview?url=";
                  if(file_ext == "doc") {
                    baseURL = "https://view.officeapps.live.com/op/embed.aspx?src=";
                  }
                  // old URL: https://docs.google.com/viewer?url=
                  this.preview_url = baseURL +
                    encodeURIComponent(data.url) +
                    "&embedded=true&headers=true";
                  // this.preview_url	= 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(data.url);
                } else {
                  this.preview_url = data.url;
                }
              } else {
                this.preview_url = data.url;
              }
            }
          })
          .catch(({ response }) => {
            if (response && response.data) {
              let data = response.data;
              if (!data.success) {
                if (response.status == 404) {
                  this.$noty.error(
                    "Error on Viewing: File not found, removed from view!"
                  );
                  this.$ebus.$emit("404-file", this.file);
                  this.$refs.view_file.close();
                } else {
                  this.$noty.error(data.message);
                }
              }
            }
          });

        this.file = file;
        if (file.is_filed) {
          this.filing_datestamp = moment(file.filing_datestamp).format(
            "D/MM/YYYY"
          );
          this.description = file.description;
        } else {
          if (file.filing_datestamp) {
            this.filing_datestamp = moment(file.filing_datestamp).format(
              "DD/MM/YYYY"
            );
          }
        }
        this.selected = { file };
        this.$refs.file_loading.close();
        this.$refs.view_file.open();
      }
    });

    this.$ebus.$on("view-ocr", (file) => {
      if (this.$refs.view_ocr != undefined) {
        this.file = file;
        this.showOcr();
        this.$refs.view_ocr.open();
      }
    });
  },

  methods: {
    openFile(file) {
      if (file.is_filed) {
        window.open(
          `/#/vault-documents/${this.$route.params.vault_id}/search?file=${file.id}`,
          "_blank"
        );
      } else {
        window.open(
          `/#/vaults/${this.$route.params.vault_id}/manage/tagging?file=${file.id}`,
          "_blank"
        );
      }
    },
    getDuplicateFile(file_id) {
      const url = `/api/vaults/${this.$route.params.vault_id}/files/${file_id}`;
      axios.get(url).then((res) => {
        if (res.data) {
          this.duplicate_file = res.data;
        }
      });
    },
    iframeLoaded() {
      if (this.preview_url != "") {
        this.iframe_loaded = true;
      }
    },
    reloadPreview(timeout = 500) {
      this.preview_loading = true;
      setTimeout(() => {
        this.preview_url = this.preview_url;
        this.preview_loading = false;
      }, timeout);
    },
    viewFileModalOpen() {
      this.iframe_reload_interval = setInterval(() => {
        if (this.iframe_loaded || this.iframe_reload_count == 10) {
          clearInterval(this.iframe_reload_interval);
          this.iframe_reload_interval = null;
          this.iframe_reload_count = 0;
        } else {
          if (this.preview_url != "") {
            this.iframe_reload_count++;
            this.reloadPreview(100);
          }
        }
      }, 6000);
      if (this.file.is_duplicate || this.file.duplicate_file_id) {
        this.getDuplicateFile(this.file.duplicate_file_id);
      }
    },
    viewFileModalClose() {
      if (document.body.hasAttribute("style")) {
        document.body.removeAttribute("style");
      }
      if (this.iframe_reload_interval) {
        clearInterval(this.iframe_reload_interval);
      }
      this.iframe_reload_count = 0;
      this.iframe_loaded = false;
      this.duplicate_file = null;
    },
    getCategoryName(category) {
      let name = category.name;
      return name.replace(/\s\g/, name);
    },
    countTagged(category) {
      let count = _.filter(this.file.tags, function (tag) {
        return tag.category_id == category.id;
      }).length;

      return count;
    },
    tagFiles(tag) {
      if (_.findIndex(this.file.tags, ["id", tag.id]) === -1) {
        axios
          .post(
            `/api/vaults/${this.$route.params.vault_id}/files/${this.file.id}/tags`,
            {
              tag_id: tag.id,
            }
          )
          .then(({ resp }) => {
            this.file.tags.push(tag);
            this.$ebus.$emit("tag-success", tag);
            this.$ebus.$emit("tagged-from-fileview", {
              tag: tag,
              file: this.file,
            });
          })
          .catch(({ response }) => {
            if (response && response.data) {
              let data = response.data;
              if (!data.success) {
                if (response.status == 404) {
                  this.$noty.error(
                    "Error on Tagging: File not found, removed from view!"
                  );
                  this.$ebus.$emit("404-file", this.file);
                  this.$ebus.$emit("remove-tag-loading");
                  this.$refs.view_file.close();
                } else {
                  this.$noty.error(data.message);
                  this.$ebus.$emit("refresh-vaults");
                  this.$ebus.$emit("remove-tag-loading");
                  this.refreshFile(tag);
                }
              }
            }
          });
      }
    },
    untagFiles(tag) {
      axios
        .delete(
          `/api/vaults/${this.$route.params.vault_id}/files/${this.file.id}/tags`,
          {
            data: {
              tag_id: tag.id,
            },
          }
        )
        .then(({ resp }) => {
          this.file.tags.splice(_.findIndex(this.file.tags, ["id", tag.id]), 1);
          this.$ebus.$emit("tag-success", tag);
          this.$ebus.$emit("untagged-from-fileview", {
            tag: tag,
            file: this.file,
          });
        })
        .catch(({ response }) => {
          if (response && response.data) {
            let data = response.data;
            if (!data.success) {
              if (response.status == 404) {
                this.$noty.error(
                  "Error on Tagging: File not found, removed from view!"
                );
                this.$ebus.$emit("404-file", this.file);
                this.$ebus.$emit("remove-tag-loading");
                this.$refs.view_file.close();
              } else {
                this.$noty.error(data.message);
              }
            }
          } else {
            this.$noty.error("Error on Tagging: please try again.");
          }
        });
    },
    showOcr() {
      axios
        .get(`api/vaults/${this.file.vault_id}/files/${this.file.id}/ocr-text`)
        .then((resp) => {
          this.ocr = "";
          if (resp.data.success) {
            this.ocr = resp.data.text;
            this.$refs.view_ocr.open();
          }
        });
    },
    download() {
      axios
        .get(
          `api/vaults/${this.file.vault_id}/files/${this.file.id}/download-path`,
          {
            params: {
              is_download: 1,
            },
          }
        )
        .then((resp) => {
          this.download_url = resp.data.url;
          window.location.href = this.download_url;
        })
        .catch(({ response }) => {
          if (response && response.data) {
            let data = response.data;
            if (!data.success) {
              if (response.status == 404) {
                this.$noty.error(
                  "Error on Downloading: File not found, removed from view!"
                );
                this.$ebus.$emit("404-file", this.file);
                this.$refs.view_file.close();
              } else {
                this.$noty.error(data.message);
              }
            }
          } else {
            this.$noty.error("Error on Downloading: please try again.");
          }
        });
    },
    closeModals() {
      this.$refs.view_file.close();
      this.$refs.view_ocr.close();
    },
    finishTagging() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (!this.selected.file.is_filed) {
            this.filing = true;
          } else {
            this.updating = true;
          }

          axios
            .patch(
              `/api/vaults/${this.$route.params.vault_id}/files/${this.selected.file.id}/mark-as-filed`,
              {
                description: this.description,
                filing_datestamp: moment(this.filing_datestamp, [
                  "DD/MM/YYYY",
                  "YYYY-MM-DD",
                ]).format("YYYY-MM-DD"),
              }
            )
            .then(({ data }) => {
              this.description = !_.isEmpty(data.description)
                ? data.description
                : "";
              this.filing_datestamp = !_.isEmpty(data.filing_datestamp)
                ? moment(data.filing_datestamp).format("D/MM/YYYY")
                : "";

              this.filing = false;
              this.updating = false;
              this.$ebus.$emit("refresh-vaults");
              this.errors.clear();

              if (this.get_next_after_filing || this.get_next_after_updating) {
                this.getFile("next");
                if (!this.selected.file.is_filed) {
                  // this.$store.commit("setFileAsRecent", data);
                  this.$ebus.$emit("update-unfiled");
                  this.$ebus.$emit("filed-from-fileview", data);
                } else {
                  this.$ebus.$emit("updated-from-fileview", data);
                }
              } else {
                if (!this.selected.file.is_filed) {
                  // this.$store.commit("setFileAsRecent", data);
                  this.$ebus.$emit("update-unfiled");
                  this.$ebus.$emit("filed-from-fileview", data);
                  this.$refs.view_file.close();
                } else {
                  this.$ebus.$emit("updated-from-fileview", data);
                }

                this.get_next_after_filing = false;
                this.get_next_after_updating = false;
              }
            })
            .catch(({ response }) => {
              if (response && response.data) {
                let data = response.data;
                this.filing = false;
                this.updating = false;
                this.errors.clear();
                if (!data.success) {
                  if (response.status == 404) {
                    this.$noty.error(
                      "Error on Filing: File not found, removed from view!"
                    );
                    this.$ebus.$emit("404-file", this.file);
                    this.$refs.view_file.close();
                  } else {
                    this.$noty.error(data.message);
                  }
                }
              }
            });
        }
      });
    },
    deleteFileConfirm() {
      if (this.file.is_filed) {
        if (!_.includes(["userA", "userB"], this.role)) {
          this.$noty.warning(
            `You don't have access to delete this file ${this.file.filename}!`,
            {
              killer: true,
              timeout: 1500,
            }
          );
        } else {
          this.$refs.delete_file_confirm.open();
        }
      } else {
        this.$refs.delete_file_confirm.open();
      }
    },
    deleteFile() {
      this.deleting = true;
      this.$refs.delete_file_confirm.close();
      axios
        .delete(`api/vaults/${this.file.vault_id}/files/${this.file.id}`)
        .then(({ data }) => {
          if (data) {
            // this.deletePermanently();
            this.deleting = false;

            // go to next file 
            this.getFile("next");

            this.$ebus.$emit("refresh-vaults");
            this.$ebus.$emit("update-unfiled");
            this.$ebus.$emit("deleted-from-fileview", "file-deleted");
            this.$ebus.$emit("remove-as-selected", this.file);
            this.$noty.success(`${this.file.filename} has been deleted!`, {
              killer: true,
              timeout: 1500,
            });

            // close if there is no next file
            if(!this.paginate.next) {
              this.$refs.view_file.close();
            }
          }
        })
        .catch(({ response }) => {
          this.$refs.delete_file_confirm.close();
          this.deleting = false;
          if (response) {
            let data = response.data;
            if (!data.success) {
              if (response.status == 404) {
                this.$noty.error(
                  "Error on Delete: File not found, removed from view!"
                );
                this.$ebus.$emit("404-file", this.file);
                this.$refs.view_file.close();
              } else {
                this.$noty.error(data.message);
              }
            }
          }
        });
    },
    deletePermanently() {
      axios
        .delete(`api/vaults/${this.file.vault_id}/files/${this.file.id}`, {
          params: {
            permanent_delete: 1,
          },
        })
        .then(({ data }) => {
          if (data) {
            this.deleting = false;
            this.$ebus.$emit("refresh-vaults");
            this.$ebus.$emit("update-unfiled");
            this.$ebus.$emit("deleted-from-fileview", "file-deleted");
            this.$ebus.$emit("remove-as-selected", this.file);
            this.$noty.success(`${this.file.filename} has been deleted!`, {
              killer: true,
              timeout: 1500,
            });
            this.$refs.view_file.close();
          }
        })
        .catch(({ response }) => {
          this.$refs.view_file.close();
          this.deleting = false;
          if (response) {
            let data = response.data;
            if (!data.success) {
              if (response.status == 404) {
                this.$noty.error(
                  "Error on Delete: File not found, removed from view!"
                );
                this.$ebus.$emit("404-file", this.file);
                this.$refs.view_file.close();
              } else {
                this.$noty.error(data.message);
              }
            }
          }
        });
    },
    getFile(direction) {
      this.$emit("navigate-files", {
        direction: direction,
        file: this.file,
      });
    },
    refreshFile(tag) {
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/files/${this.file.id}`)
        .then(({ data }) => {
          if (!_.isEmpty(data)) {
            let file = data;
            this.file = file;
            this.selected = { file };
            this.$ebus.$emit("tagged-from-fileview", {
              tag: tag,
              file: this.file,
            });
          }
        });
    },
    getCurrentStateFilePermissions() {
      if (this.file.is_filed) {
        return "can_delete_document_after_filing";
      } else {
        return "can_delete_document_before_filing";
      }
    },
  },

  computed: {
    set_modal_width() {
      if (this.$currentViewport.label == "mobile") {
        return "100%";
      } else {
        return "80%";
      }
    },
    uploaded_date_simplified() {
      let now = moment();
      let file_date = moment(this.file.created_at);
      return file_date.fromNow();
    },
    uploaded_date_formatted() {
      let file_date = moment(this.file.created_at);
      return file_date.format("dddd, MMMM Do YYYY, h:mm:ss a");
    },
    document_date() {
      let file_date = moment(this.file.filing_datestamp);
      return file_date.format("MMMM D, YYYY");
    },
    file_size() {
      if (this.file.size == 0) return "0 Bytes";
      var k = 1000,
        dm = 2 || 2,
        sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
        i = Math.floor(Math.log(this.file.size) / Math.log(k));
      return (
        parseFloat((this.file.size / Math.pow(k, i)).toFixed(dm)) + sizes[i]
      );
    },
    ...mapGetters({
      categorized_tags: "getCategorizedTags",
      role_id: "getUserRoleId",
      role: "getUserRole",
    }),
  },
};
</script>

<style>
.tag-height {
  height: 350px;
  min-height: 350px;
}
.file-preview .sweet-content {
  padding: 0 !important;
}
#file-modal .sweet-modal {
  max-width: 100% !important;
}
@media (min-width: 768px) {
  .tag-height {
    height: 350px;
    min-height: 350px;
  }
}

@media (max-width: 768px) {
  .iframe {
    height: 75vh;
  }
}
.loading-indicator {
  background: rgba(218, 222, 231, 0.7);
}
</style>
